import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Check, Car } from "lucide-react";
import { SofiAvatar } from "./SofiAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderModal({ open, onClose, context }) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [messenger, setMessenger] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  if (!open) return null;

  async function submit() {
    if (!name.trim() || !contact.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API}/orders`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: context.sessionId, customer_name: name, contact,
          mode: context.mode || "url",
          email: email || null, messenger: messenger || null,
          vehicle: {
            url: context.sourceUrl || null, source: context.source || null,
            make: context.make, model: context.model, year: context.year,
            listing_price: context.listing_price, currency: context.currency || "EUR",
          },
          final_price_uah: context.final_price_uah, sofi_score: context.sofi_score,
          recommendation_uk: context.recommendation_uk, report_id: context.report_id,
          report_snapshot: context.report_snapshot || {},
        }),
      });
      if (!r.ok) throw new Error("Не вдалося надіслати заявку");
      setDone(await r.json());
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
        onClick={onClose} data-testid="order-modal">
        <motion.div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d0d0d] p-6"
          initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}>
          <div className="mb-4 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <SofiAvatar size={40} state="happy" ringWidth={1} />
              <div>
                <h3 className="font-heading text-lg font-bold text-white">Замовити довіз через ADT</h3>
                <p className="text-xs text-white/50">Ні до чого не зобов'язує</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="order-close" className="rounded-lg p-1.5 text-white/40 hover:bg-white/5 hover:text-white">
              <X size={18} />
            </button>
          </div>

          {done ? (
            <div className="py-6 text-center" data-testid="order-success">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#22c55e]/15">
                <Check className="h-7 w-7 text-[#22c55e]" />
              </div>
              <p className="text-[15px] leading-relaxed text-white/85">{done.message_uk}</p>
              <button onClick={onClose} data-testid="order-done-btn"
                className="mt-5 rounded-xl bg-[#C73D3A] px-6 py-2.5 font-semibold text-white hover:bg-[#d84a47]">
                Готово
              </button>
            </div>
          ) : (
            <>
              {(context.make || context.model) && (
                <div className="mb-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/80">
                  <Car size={16} className="text-[#C73D3A]" />
                  {context.make} {context.model} {context.year}
                  {context.final_price_uah != null && (
                    <span className="ml-auto font-heading font-bold text-white">
                      {new Intl.NumberFormat("uk-UA").format(Math.round(context.final_price_uah))} ₴
                    </span>
                  )}
                </div>
              )}
              <label className="mb-3 block">
                <span className="mb-1 block text-xs text-white/50">Ваше ім'я</span>
                <input data-testid="order-name" className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ім'я" />
              </label>
              <label className="mb-3 block">
                <span className="mb-1 block text-xs text-white/50">Телефон *</span>
                <input data-testid="order-contact" className="inp" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="+380…" />
              </label>
              <div className="mb-4 grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-xs text-white/50">E-mail</span>
                  <input data-testid="order-email" className="inp" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@…" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs text-white/50">Telegram / Viber</span>
                  <input data-testid="order-messenger" className="inp" value={messenger} onChange={(e) => setMessenger(e.target.value)} placeholder="@нік / номер" />
                </label>
              </div>
              {error && <p className="mb-3 text-sm text-[#C73D3A]" data-testid="order-error">{error}</p>}
              <button data-testid="order-submit" onClick={submit} disabled={busy || !name.trim() || !contact.trim()}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#C73D3A] px-5 py-3.5 font-heading font-semibold tracking-wide text-white transition-colors hover:bg-[#d84a47] disabled:opacity-50">
                {busy ? <><Loader2 size={18} className="animate-spin" /> Надсилаємо…</> : <>🚗 Надіслати заявку</>}
              </button>
              <p className="mt-3 text-center text-xs text-white/40">Менеджер ADT зв'яжеться з вами особисто.</p>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
